import { createError, RelodeApiError } from "@relode/core";

import { CombinedCandidatesFeatureActionTypes } from "..";
import { converters } from "../../../../shared/backend";
import { candidates } from "../../../../shared/backend/controllers";
import { CandidateState } from "../candidateState";
import { creators } from "./candidateActionTypes";

export const useCandidateActions = (
    state: CandidateState,
    dispatch: React.Dispatch<CombinedCandidatesFeatureActionTypes>
) => {
    const loadCandidate = async (jobId: number, leadId: number) => {
        try {
            dispatch(creators.loadCandidate());
            const detailsResponse = await candidates.candidateService.getCandidateDetails(
                jobId,
                leadId
            );
            const details = converters.convertLeadJobDetailResponse(
                detailsResponse
            );
            dispatch(creators.loadCandidateSuccess(jobId, leadId, details));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.loadCandidateFailure(err));
                    return;
                }
            }
            throw createError(
                err,
                "client.candidates.candidateActions.loadCandidate"
            );
        }
    };

    const addFeedback = async (
        jobId: number,
        leadId: number,
        vote: boolean,
        comment: string
    ) => {
        try {
            dispatch(creators.manageCandidateFeedback());
            await candidates.feedbackService.addFeedback(jobId, leadId, {
                vote,
                comment,
            });
            await loadCandidate(jobId, leadId);
            dispatch(creators.manageCandidateFeedbackSuccess("feedback saved"));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.manageCandidateFeedbackFailure(err));
                    return;
                }
            }
            throw createError(
                err,
                "client.candidates.candidateActions.addFeedback"
            );
        }
    };

    const updateFeedback = async (
        jobId: number,
        leadId: number,
        vote: boolean,
        comment: string
    ) => {
        try {
            dispatch(creators.manageCandidateFeedback());
            await candidates.feedbackService.updateFeedback(jobId, leadId, {
                vote,
                comment,
            });
            await loadCandidate(jobId, leadId);
            dispatch(
                creators.manageCandidateFeedbackSuccess("feedback updated")
            );
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.manageCandidateFeedbackFailure(err));
                    return;
                }
            }
            throw createError(
                err,
                "client.candidates.candidateActions.updateFeedback"
            );
        }
    };

    const loadCandidateShare = async (jobId: number, leadId: number) => {
        try {
            dispatch(creators.loadCandidateShare());
            const detailsResponse = await candidates.candidateService.getCandidateShareLink(
                jobId,
                leadId
            );
            const details = converters.convertShareCandidateResponse(
                detailsResponse
            );
            dispatch(creators.loadCandidateShareSuccess(details));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    err.friendlyMessages = "Unable to retrieve share URL.";
                    dispatch(creators.loadCandidateShareFailure(err));
                    return;
                }
            }
            throw createError(
                err,
                "client.candidates.candidateActions.loadCandidateShare"
            );
        }
    };

    return {
        loadCandidate,
        loadCandidateShare,
        addFeedback,
        updateFeedback,
    };
};

export type CandidateActions = ReturnType<typeof useCandidateActions>;
